import React, { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Table,
  Button,
  Input,
  Space,
  Typography,
  Spin,
  Alert,
  Drawer,
  Empty,
  Breadcrumb,
  Tooltip,
  Tabs,
  Radio,
  Select,
} from 'antd';
import {
  SearchOutlined,
  FolderOutlined,
  FileOutlined,
  DownloadOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  EyeOutlined,
  CloudOutlined,
  FileImageOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  FileZipOutlined,
  FileUnknownOutlined,
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  DotChartOutlined,
  TableOutlined,
} from '@ant-design/icons';
import { listS3Objects, getSignedUrl, getS3Object, listBuckets, getCsvFromS3, getJsonFromS3 } from '../utils/s3Utils';
import ReactECharts from 'echarts-for-react';
import '../css/S3Explorer.css';
import '../css/S3DataVisualizer.css';
import '../css/ModernDataVisualization.css';
import '../css/ModernTabs.css';

const { Content } = Layout;
const { Title } = Typography;
const { Search } = Input;

// Interface pour les éléments S3 (dossiers et fichiers)
interface S3Item {
  key: string;
  name: string;
  size?: number;
  lastModified?: Date;
  isFolder: boolean;
}

// Interface pour les buckets S3
interface S3Bucket {
  name: string;
  creationDate?: Date;
}

// Interface pour les options de visualisation
interface VisualizationOptions {
  chartType: 'bar' | 'line' | 'pie' | 'scatter';
  xAxis: string;
  yAxis: string;
  viewMode: 'table' | 'chart';
  categoryField?: string;
}

// Interface pour les données à visualiser
interface DataItem {
  [key: string]: any;
}

const SimpleS3Explorer: React.FC = () => {
  // État pour le mode d'affichage (buckets ou contenu d'un bucket)
  const [viewMode, setViewMode] = useState<'buckets' | 'bucket-content'>('buckets');

  // État pour les buckets S3
  const [buckets, setBuckets] = useState<S3Bucket[]>([]);
  const [selectedBucket, setSelectedBucket] = useState<string>('');

  // État pour le chemin actuel dans le bucket
  const [currentPath, setCurrentPath] = useState<string>('');
  const [breadcrumbs, setBreadcrumbs] = useState<string[]>(['']);

  // État pour les objets S3
  const [s3Items, setS3Items] = useState<S3Item[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // État pour la prévisualisation des fichiers
  const [previewVisible, setPreviewVisible] = useState<boolean>(false);
  const [previewItem, setPreviewItem] = useState<S3Item | null>(null);
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // État pour la visualisation des données
  const [fileData, setFileData] = useState<DataItem[]>([]);
  const [dataColumns, setDataColumns] = useState<string[]>([]);
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [visualizationOptions, setVisualizationOptions] = useState<VisualizationOptions>({
    chartType: 'bar',
    xAxis: '',
    yAxis: '',
    viewMode: 'table',
    categoryField: undefined,
  });
  const [activeTab, setActiveTab] = useState<string>('preview');
  const [searchDataText, setSearchDataText] = useState<string>('');

  // État pour le filtre de recherche
  const [searchText, setSearchText] = useState<string>('');

  // Fonction pour charger les objets S3 depuis un bucket spécifique
  const loadS3Objects = async (bucket: string, path: string = '') => {
    try {
      setLoading(true);
      setError(null);

      console.log(`Loading S3 objects from bucket ${bucket} with prefix: ${path}`);
      const objects = await listS3Objects(bucket, path);

      // Transform S3 objects into items for the interface
      const items: S3Item[] = [];

      // Create a set to track unique folders
      const folders = new Set<string>();

      // Process each S3 object
      objects.forEach((obj) => {
        if (obj.Key) {
          // Remove the prefix from the current path
          const relativePath = obj.Key.startsWith(path) ? obj.Key.slice(path.length) : obj.Key;

          // Check if it's a folder or a file
          if (relativePath.includes('/')) {
            // It's an item in a subfolder
            const folderName = relativePath.split('/')[0] + '/';

            // Add the folder if it doesn't exist yet
            if (!folders.has(folderName)) {
              folders.add(folderName);
              items.push({
                key: path + folderName,
                name: folderName,
                isFolder: true,
              });
            }
          } else if (relativePath !== '') {
            // It's a file in the current folder
            items.push({
              key: obj.Key,
              name: relativePath,
              size: obj.Size,
              lastModified: obj.LastModified,
              isFolder: false,
            });
          }
        }
      });

      setS3Items(items);
    } catch (error) {
      console.error('Error loading S3 objects:', error);
      setError(`Failed to load S3 objects: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour charger la liste des buckets
  const loadBuckets = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Loading S3 buckets...');
      const s3Buckets = await listBuckets();

      // Transform buckets into items for the interface
      const bucketItems: S3Bucket[] = s3Buckets.map((bucket) => ({
        name: bucket.Name || '',
        creationDate: bucket.CreationDate,
      }));

      setBuckets(bucketItems);
    } catch (error) {
      console.error('Error loading S3 buckets:', error);
      setError(`Failed to load S3 buckets: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  // Charger les buckets ou le contenu du bucket sélectionné au chargement du composant
  useEffect(() => {
    if (viewMode === 'buckets') {
      loadBuckets();
    } else if (viewMode === 'bucket-content' && selectedBucket) {
      loadS3Objects(selectedBucket, currentPath);
    }
  }, [viewMode, selectedBucket, currentPath]);

  // Fonction pour sélectionner un bucket
  const selectBucket = (bucketName: string) => {
    setSelectedBucket(bucketName);
    setCurrentPath('');
    setBreadcrumbs(['']);
    setViewMode('bucket-content');
    loadS3Objects(bucketName, '');
  };

  // Fonction pour revenir à la liste des buckets
  const backToBuckets = () => {
    setViewMode('buckets');
    setSelectedBucket('');
    setCurrentPath('');
    setBreadcrumbs(['']);
    loadBuckets();
  };

  // Fonction pour naviguer vers un dossier
  const navigateToFolder = (folderPath: string) => {
    setCurrentPath(folderPath);
    setBreadcrumbs(['', ...folderPath.split('/').filter((p) => p !== '')]);
    loadS3Objects(selectedBucket, folderPath);
  };

  // Fonction pour naviguer vers le dossier parent
  const navigateToParent = () => {
    if (currentPath === '') {
      backToBuckets();
      return;
    }

    const pathParts = currentPath.split('/');
    pathParts.pop(); // Supprimer le dernier élément (dossier actuel)
    pathParts.pop(); // Supprimer l'élément vide après le dernier slash
    const parentPath = pathParts.join('/') + (pathParts.length > 0 ? '/' : '');

    setCurrentPath(parentPath);
    setBreadcrumbs(['', ...parentPath.split('/').filter((p) => p !== '')]);
    loadS3Objects(selectedBucket, parentPath);
  };

  // Fonction pour prévisualiser un fichier
  const previewFile = async (item: S3Item) => {
    if (item.isFolder) return;

    try {
      setPreviewVisible(true);
      setPreviewItem(item);
      setPreviewLoading(true);
      setPreviewError(null);
      setActiveTab('preview');

      const s3ObjectBlob = await getS3Object(item.key); // Renamed to s3ObjectBlob for clarity

      // Changed: Check the size of the Blob instead of a non-existent 'Body' property
      if (s3ObjectBlob.size === 0) {
        throw new Error('File content is empty');
      }

      // Changed: Directly transform the Blob to a string
      const content = await s3ObjectBlob.text(); // Use .text() method on the Blob
      setPreviewContent(content);

      // Si c'est un fichier CSV ou JSON, charger également les données pour visualisation
      if (item.key.endsWith('.csv') || item.key.endsWith('.json')) {
        loadDataForVisualization(item);
      }
    } catch (error) {
      console.error('Error previewing file:', error);
      setPreviewError(`Failed to preview file: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Fonction pour charger les données pour la visualisation
  const loadDataForVisualization = async (item: S3Item) => {
    try {
      setDataLoading(true);
      setDataError(null);

      let data: DataItem[] = [];

      if (item.key.endsWith('.csv')) {
        data = await getCsvFromS3(item.key);
      } else if (item.key.endsWith('.json')) {
        data = await getJsonFromS3(item.key);
      } else {
        throw new Error('Unsupported file type for visualization');
      }

      if (data && data.length > 0) {
        setFileData(data);

        // Extraire les colonnes des données
        const columns = Object.keys(data[0]);
        setDataColumns(columns);

        // Définir les axes par défaut s'ils ne sont pas déjà définis
        if (!visualizationOptions.xAxis && columns.length > 0) {
          setVisualizationOptions((prev) => ({
            ...prev,
            xAxis: columns[0],
            yAxis: columns.length > 1 ? columns[1] : columns[0],
          }));
        }
      } else {
        throw new Error('No data found in file');
      }
    } catch (error) {
      console.error('Error loading data for visualization:', error);
      setDataError(`Failed to load data: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setDataLoading(false);
    }
  };

  // Générer les options pour le graphique ECharts
  const getChartOptions = () => {
    if (!fileData.length || !visualizationOptions.xAxis || !visualizationOptions.yAxis) {
      return {};
    }

    const { chartType, xAxis, yAxis, categoryField } = visualizationOptions;

    // Extraire les valeurs pour les axes
    const xAxisData = fileData.map((item) => item[xAxis]);
    const yAxisData = fileData.map((item) => item[yAxis]);

    // Options de base pour tous les types de graphiques
    const baseOptions = {
      title: {
        show: false,
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
      },
      toolbox: {
        feature: {
          saveAsImage: { title: 'Save as Image' },
          dataZoom: { title: 'Zoom' },
          restore: { title: 'Restore' },
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        name: xAxis,
        nameLocation: 'middle',
        nameGap: 30,
      },
      yAxis: {
        type: 'value',
        name: yAxis,
        nameLocation: 'middle',
        nameGap: 50,
      },
      dataZoom: [
        {
          type: 'inside',
          start: 0,
          end: 100,
        },
        {
          start: 0,
          end: 100,
        },
      ],
      animation: true,
    };

    // Options spécifiques selon le type de graphique
    switch (chartType) {
      case 'bar':
        return {
          ...baseOptions,
          series: [
            {
              data: yAxisData,
              type: 'bar',
              itemStyle: {
                color: '#1890ff',
              },
              emphasis: {
                itemStyle: {
                  color: '#40a9ff',
                },
              },
            },
          ],
        };

      case 'line':
        return {
          ...baseOptions,
          series: [
            {
              data: yAxisData,
              type: 'line',
              smooth: true,
              symbol: 'circle',
              symbolSize: 6,
              lineStyle: {
                width: 2,
                color: '#1890ff',
              },
              itemStyle: {
                color: '#1890ff',
              },
              areaStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    {
                      offset: 0,
                      color: 'rgba(24, 144, 255, 0.6)',
                    },
                    {
                      offset: 1,
                      color: 'rgba(24, 144, 255, 0.1)',
                    },
                  ],
                },
              },
            },
          ],
        };

      case 'pie':
        // Pour le graphique circulaire, nous avons besoin de paires nom-valeur
        const pieData = xAxisData.map((name, index) => ({
          name: String(name),
          value: yAxisData[index],
        }));

        return {
          tooltip: {
            trigger: 'item',
            formatter: '{a} <br/>{b}: {c} ({d}%)',
          },
          legend: {
            orient: 'vertical',
            left: 'left',
            data: xAxisData.map(String),
          },
          series: [
            {
              name: yAxis,
              type: 'pie',
              radius: '60%',
              center: ['50%', '50%'],
              data: pieData,
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowOffsetX: 0,
                  shadowColor: 'rgba(0, 0, 0, 0.5)',
                },
              },
              label: {
                formatter: '{b}: {d}%',
              },
            },
          ],
        };

      case 'scatter':
        let seriesData = [];

        // Si un champ de catégorie est spécifié, créer plusieurs séries
        if (categoryField) {
          // Regrouper les données par catégorie
          const categorizedData: Record<string, { x: any; y: any }[]> = {};

          fileData.forEach((item) => {
            const category = String(item[categoryField] || 'Unknown');
            if (!categorizedData[category]) {
              categorizedData[category] = [];
            }

            categorizedData[category].push({
              x: item[xAxis],
              y: item[yAxis],
            });
          });

          // Créer une série pour chaque catégorie
          seriesData = Object.entries(categorizedData).map(([category, points]) => ({
            name: category,
            type: 'scatter',
            data: points.map((p) => [p.x, p.y]),
            symbolSize: 10,
          }));
        } else {
          // Une seule série avec toutes les données
          seriesData = [
            {
              type: 'scatter',
              data: fileData.map((item) => [item[xAxis], item[yAxis]]),
              symbolSize: 10,
              itemStyle: {
                color: '#1890ff',
              },
            },
          ];
        }

        return {
          ...baseOptions,
          xAxis: { ...baseOptions.xAxis, type: 'value' },
          legend: categoryField ? {} : { show: false },
          series: seriesData,
        };

      default:
        return baseOptions;
    }
  };

  // Générer les colonnes pour le tableau de données
  const getTableColumns = () => {
    return dataColumns.map((column) => ({
      title: column,
      dataIndex: column,
      key: column,
      sorter: (a: any, b: any) => {
        if (typeof a[column] === 'number' && typeof b[column] === 'number') {
          return a[column] - b[column];
        }
        return String(a[column]).localeCompare(String(b[column]));
      },
      render: (text: any) => {
        if (text === null || text === undefined) return '-';
        if (typeof text === 'object') return JSON.stringify(text);
        return text;
      },
    }));
  };

  // Filtrer les données en fonction de la recherche
  const filteredData = searchDataText
    ? fileData.filter((item) =>
      Object.values(item).some((value) => String(value).toLowerCase().includes(searchDataText.toLowerCase())),
    )
    : fileData;

  // Fonction pour télécharger un fichier
  const downloadFile = async (item: S3Item) => {
    try {
      const url = await getSignedUrl(item.key);

      // Créer un lien temporaire pour télécharger le fichier
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', item.name);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  // Filtrer les éléments en fonction de la recherche
  const filteredItems = searchText
    ? s3Items.filter((item) => item.name.toLowerCase().includes(searchText.toLowerCase()))
    : s3Items;

  // Fonction pour obtenir l'icône appropriée pour un type de fichier
  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';

    switch (extension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'svg':
        return <FileImageOutlined style={{ color: '#1890ff', fontSize: '18px' }} />;
      case 'txt':
      case 'log':
      case 'md':
      case 'json':
      case 'xml':
      case 'html':
      case 'css':
      case 'js':
        return <FileTextOutlined style={{ color: '#52c41a', fontSize: '18px' }} />;
      case 'pdf':
        return <FilePdfOutlined style={{ color: '#f5222d', fontSize: '18px' }} />;
      case 'xlsx':
      case 'xls':
      case 'csv':
        return <FileExcelOutlined style={{ color: '#52c41a', fontSize: '18px' }} />;
      case 'zip':
      case 'rar':
      case 'tar':
      case 'gz':
        return <FileZipOutlined style={{ color: '#faad14', fontSize: '18px' }} />;
      default:
        return <FileOutlined style={{ color: '#1890ff', fontSize: '18px' }} />;
    }
  };

  // Colonnes pour la table des fichiers
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: S3Item) => (
        <Space>
          {record.isFolder ? <FolderOutlined style={{ color: '#faad14', fontSize: '18px' }} /> : getFileIcon(text)}
          {record.isFolder ? (
            <Button type='link' onClick={() => navigateToFolder(record.key)} style={{ padding: 0, height: 'auto' }}>
              {text}
            </Button>
          ) : (
            <span>{text}</span>
          )}
        </Space>
      ),
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      render: (size?: number) => (size ? `${(size / 1024).toFixed(2)} KB` : '-'),
    },
    {
      title: 'Last Modified',
      dataIndex: 'lastModified',
      key: 'lastModified',
      render: (date?: Date) => (date ? date.toLocaleString() : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: S3Item) => (
        <Space>
          {!record.isFolder && (
            <>
              <Tooltip title='Download file'>
                <Button
                  type='primary'
                  shape='circle'
                  icon={<DownloadOutlined />}
                  onClick={() => downloadFile(record)}
                  size='small'
                />
              </Tooltip>
              <Tooltip title='Preview file'>
                <Button
                  type='primary'
                  shape='circle'
                  icon={<EyeOutlined />}
                  onClick={() => previewFile(record)}
                  size='small'
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  const renderContent = () => {
    if (viewMode === 'buckets') {
      // Columns for the buckets list
      const bucketColumns = [
        {
          title: 'Bucket Name',
          dataIndex: 'name',
          key: 'name',
          render: (text: string) => (
            <Button type='link' onClick={() => selectBucket(text)}>
              {text}
            </Button>
          ),
        },
        {
          title: 'Creation Date',
          dataIndex: 'creationDate',
          key: 'creationDate',
          render: (date?: Date) => (date ? date.toLocaleString() : '-'),
        },
      ];

      return (
        <>
          <Title level={4}>S3 Buckets List</Title>
          <div style={{ width: '100%' }}>
            <Table
              columns={bucketColumns}
              dataSource={buckets}
              rowKey='name'
              loading={loading}
              pagination={{
                pageSize: 10,
                size: 'small',
                showTotal: (total) => `Total: ${total} buckets`,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50'],
                position: ['topRight'],
              }}
              className='s3-table modern-table'
              locale={{
                emptyText: <Empty description='No buckets found' image={Empty.PRESENTED_IMAGE_SIMPLE} />,
              }}
              rowClassName={() => 'table-row-hover'}
            />
          </div>
        </>
      );
    } else {
      return (
        <>
          <Space direction='vertical' style={{ width: '100%', marginBottom: 16 }}>
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={navigateToParent}>
                {currentPath === '' ? 'Back to buckets' : 'Back'}
              </Button>
              <Breadcrumb>
                <Breadcrumb.Item>
                  <Button type='link' onClick={backToBuckets}>
                    Buckets
                  </Button>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                  <Button type='link' onClick={() => navigateToFolder('')}>
                    {selectedBucket}
                  </Button>
                </Breadcrumb.Item>
                {breadcrumbs.slice(1).map((part, index) => (
                  <Breadcrumb.Item key={index}>
                    {part !== '' ? (
                      <Button
                        type='link'
                        onClick={() => navigateToFolder(breadcrumbs.slice(1, index + 2).join('/') + '/')}
                      >
                        {part}
                      </Button>
                    ) : null}
                  </Breadcrumb.Item>
                ))}
              </Breadcrumb>
            </Space>

            <Search
              placeholder='Search files...'
              allowClear
              enterButton={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Space>

          <div className='s3-table-container'>
            <Table
              columns={columns}
              dataSource={filteredItems}
              rowKey='key'
              loading={loading}
              pagination={{
                pageSize: 15,
                size: 'small',
                showTotal: (total) => `Total: ${total} items`,
                showSizeChanger: true,
                pageSizeOptions: ['15', '30', '50'],
                position: ['topRight'],
              }}
              className='s3-table modern-table'
              locale={{
                emptyText: <Empty description='No files found' image={Empty.PRESENTED_IMAGE_SIMPLE} />,
              }}
              rowClassName={() => 'table-row-hover'}
            />
          </div>
        </>
      );
    }
  };

  return (
    <Layout style={{ width: '100%', background: '#f0f2f5' }}>
      <Content className='s3-explorer-content' style={{ height: '100%' }}>
        <Card
          title={
            <Space>
              <CloudOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
              <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
                {viewMode === 'buckets' ? 'S3 Explorer' : `S3 Explorer - ${selectedBucket}`}
              </span>
            </Space>
          }
          className='s3-explorer-card'
          extra={
            <Button
              icon={<ReloadOutlined />}
              onClick={() => (viewMode === 'buckets' ? loadBuckets() : loadS3Objects(selectedBucket, currentPath))}
              loading={loading}
            >
              Refresh
            </Button>
          }
        >
          {/* Error display */}
          {error && <Alert message='Error' description={error} type='error' showIcon style={{ marginBottom: 16 }} />}

          {/* Main content */}
          {renderContent()}
        </Card>
        <Drawer
          title={
            <Space>
              {previewItem?.isFolder ? (
                <FolderOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
              ) : previewItem ? (
                getFileIcon(previewItem.name)
              ) : (
                <FileUnknownOutlined />
              )}
              <span style={{ fontWeight: 'bold' }}>{previewItem?.name || 'Preview'}</span>
            </Space>
          }
          placement='right'
          width='50%'
          onClose={() => {
            setPreviewVisible(false);
            setPreviewContent('');
            setPreviewError(null);
            setPreviewItem(null);
            setFileData([]);
            setDataColumns([]);
            setDataError(null);
            setActiveTab('preview');
            setSearchDataText('');
          }}
          open={previewVisible}
          styles={{
            body: {
              padding: '16px',
              overflowY: 'auto',
              background: '#f9f9f9',
            },
            header: {
              borderBottom: '1px solid #f0f0f0',
              padding: '16px 24px',
            },
            wrapper: {
              height: '100%',
            },
            content: {
              boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.15)',
            },
          }}
          extra={
            previewItem && (
              <Tooltip title='Download file'>
                <Button
                  type='primary'
                  shape='round'
                  icon={<DownloadOutlined />}
                  onClick={() => downloadFile(previewItem)}
                >
                  Download
                </Button>
              </Tooltip>
            )
          }
        >
          {previewLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Spin size='large' tip='Loading file...' />
            </div>
          ) : previewError ? (
            <Alert message='Error' description={previewError} type='error' showIcon />
          ) : (
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              type='card'
              className='modern-tabs'
              items={[
                {
                  key: 'preview',
                  label: (
                    <span>
                      <FileTextOutlined /> Raw Preview
                    </span>
                  ),
                  children: (
                    <div
                      style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', overflow: 'auto' }}
                    >
                      {previewContent}
                    </div>
                  ),
                },
                {
                  key: 'visualization',
                  label: (
                    <span>
                      <BarChartOutlined /> Data Visualization
                    </span>
                  ),
                  disabled: !(fileData && fileData.length > 0),
                  children: (
                    <div className='data-visualization-container'>
                      {dataLoading ? (
                        <div
                          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}
                        >
                          <Spin size='large' tip='Loading data...' />
                        </div>
                      ) : dataError ? (
                        <Alert message='Error' description={dataError} type='error' showIcon />
                      ) : fileData.length > 0 ? (
                        <div>
                          <Card className='modern-card' style={{ marginBottom: 16 }}>
                            <Space direction='vertical' style={{ width: '100%' }} size='middle'>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Radio.Group
                                  value={visualizationOptions.viewMode}
                                  onChange={(e) =>
                                    setVisualizationOptions((prev) => ({ ...prev, viewMode: e.target.value }))
                                  }
                                  buttonStyle='solid'
                                >
                                  <Radio.Button value='table'>
                                    <TableOutlined /> Table
                                  </Radio.Button>
                                  <Radio.Button value='chart'>
                                    <DotChartOutlined /> Chart
                                  </Radio.Button>
                                </Radio.Group>

                                {visualizationOptions.viewMode === 'chart' && (
                                  <Radio.Group
                                    value={visualizationOptions.chartType}
                                    onChange={(e) =>
                                      setVisualizationOptions((prev) => ({ ...prev, chartType: e.target.value }))
                                    }
                                    buttonStyle='solid'
                                  >
                                    <Radio.Button value='bar'>
                                      <BarChartOutlined /> Bar
                                    </Radio.Button>
                                    <Radio.Button value='line'>
                                      <LineChartOutlined /> Line
                                    </Radio.Button>
                                    <Radio.Button value='scatter'>
                                      <DotChartOutlined /> Scatter
                                    </Radio.Button>
                                    <Radio.Button value='pie'>
                                      <PieChartOutlined /> Pie
                                    </Radio.Button>
                                  </Radio.Group>
                                )}
                              </div>

                              <Input.Search
                                placeholder='Search in data...'
                                value={searchDataText}
                                onChange={(e) => setSearchDataText(e.target.value)}
                                style={{ width: '100%' }}
                                allowClear
                              />

                              {visualizationOptions.viewMode === 'chart' && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                  <Select
                                    style={{ width: 200 }}
                                    placeholder='Select X-Axis'
                                    value={visualizationOptions.xAxis}
                                    onChange={(value) => setVisualizationOptions((prev) => ({ ...prev, xAxis: value }))}
                                    className='modern-select'
                                  >
                                    {dataColumns.map((column) => (
                                      <Select.Option key={column} value={column}>
                                        {column}
                                      </Select.Option>
                                    ))}
                                  </Select>

                                  <Select
                                    style={{ width: 200 }}
                                    placeholder='Select Y-Axis'
                                    value={visualizationOptions.yAxis}
                                    onChange={(value) => setVisualizationOptions((prev) => ({ ...prev, yAxis: value }))}
                                    className='modern-select'
                                  >
                                    {dataColumns.map((column) => (
                                      <Select.Option key={column} value={column}>
                                        {column}
                                      </Select.Option>
                                    ))}
                                  </Select>

                                  {visualizationOptions.chartType === 'scatter' && (
                                    <Select
                                      style={{ width: 200 }}
                                      placeholder='Select Category Field (Optional)'
                                      value={visualizationOptions.categoryField}
                                      onChange={(value) =>
                                        setVisualizationOptions((prev) => ({ ...prev, categoryField: value }))
                                      }
                                      allowClear
                                      className='modern-select'
                                    >
                                      {dataColumns.map((column) => (
                                        <Select.Option key={column} value={column}>
                                          {column}
                                        </Select.Option>
                                      ))}
                                    </Select>
                                  )}
                                </div>
                              )}
                            </Space>
                          </Card>

                          {visualizationOptions.viewMode === 'table' ? (
                            <div className='data-table-container'>
                              <Table
                                dataSource={filteredData}
                                columns={getTableColumns()}
                                rowKey={(record, index) => index?.toString() || '0'}
                                scroll={{ x: 'max-content', y: 400 }}
                                pagination={{ pageSize: 10 }}
                                size='middle'
                                className='modern-table'
                              />
                            </div>
                          ) : (
                            <div className='chart-container' style={{ height: 400, marginTop: 16 }}>
                              <ReactECharts
                                option={getChartOptions()}
                                style={{ height: '100%', width: '100%' }}
                                opts={{ renderer: 'canvas' }}
                                className='modern-chart'
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        <Empty description='No data available for visualization' />
                      )}
                    </div>
                  ),
                },
              ]}
            />
          )}
        </Drawer>
      </Content>
    </Layout>
  );
};

export default SimpleS3Explorer;
